<script setup lang="ts">
/**
 * THROWAWAY proof-of-concept page for the GroupDocs.Viewer rendering spike.
 * See `_planning/raw_file_rendering_groupdocs.md` ("Lowest-effort POC", Step 2).
 *
 * Pick a sample file from the gitignored `temp_raw_data/` folder and view it
 * rendered inside an <iframe> — the SPA's real iframe sandbox surface. PDFs
 * stream as application/pdf (browser-native viewer); DOCX/XLSX/PPTX render to
 * HTML server-side via GroupDocs.Viewer.
 *
 * Delete this component, its route, and the backend poc_render_router once the
 * spike concludes.
 */
import { ref, computed, watch, onMounted } from 'vue';
import { useAuthStore } from '../../stores/authStore';

const auth = useAuthStore();
const files = ref<string[]>([]);
// Start empty — no file pre-selected, no iframe shown on first landing.
const selected = ref<string>('');
const loadError = ref<string | null>(null);
const rendering = ref(false);

// The render/download endpoints are auth-gated, but <iframe src> and
// <a download> are direct browser navigations that can't send the bearer
// header — so carry the token as a query param (the backend accepts either).
function withAuth(url: string): string {
    return auth.token
        ? `${url}&access_token=${encodeURIComponent(auth.token)}`
        : url;
}

const frameSrc = computed(() =>
    selected.value
        ? withAuth(`/api/poc/render?name=${encodeURIComponent(selected.value)}`)
        : '',
);

// Original (unrendered) file, for side-by-side fidelity comparison.
const downloadUrl = computed(() =>
    selected.value
        ? withAuth(`/api/poc/download?name=${encodeURIComponent(selected.value)}`)
        : '',
);

// Show the loading indicator from the moment a file is picked until the
// iframe finishes loading the rendered output (onFrameLoad clears it).
watch(selected, () => {
    rendering.value = Boolean(selected.value);
});

function onFrameLoad() {
    rendering.value = false;
}

onMounted(async () => {
    try {
        const res = await fetch('/api/poc/samples');
        if (!res.ok) throw new Error(`samples request failed (${res.status})`);
        const data = await res.json();
        files.value = data.files ?? [];
    } catch (err) {
        loadError.value = err instanceof Error ? err.message : String(err);
    }
});
</script>

<template>
    <div class="poc-page">
        <header class="poc-bar">
            <strong>POC · raw-file render</strong>
            <select v-model="selected" :disabled="!files.length">
                <option value="" disabled>Select a file…</option>
                <option v-for="f in files" :key="f" :value="f">{{ f }}</option>
            </select>
            <a v-if="frameSrc" :href="frameSrc" target="_blank" rel="noopener">
                open in new tab ↗
            </a>
            <a v-if="downloadUrl" :href="downloadUrl" download>
                download original ↓
            </a>
            <span v-if="loadError" class="poc-error">{{ loadError }}</span>
        </header>

        <div v-if="frameSrc" class="poc-frame-wrap">
            <div v-if="rendering" class="poc-loading">
                <span class="poc-spinner" aria-hidden="true"></span>
                <span>Rendering “{{ selected }}”…</span>
            </div>
            <iframe
                :key="frameSrc"
                :src="frameSrc"
                class="poc-frame"
                sandbox="allow-same-origin allow-scripts allow-popups"
                title="Rendered document"
                @load="onFrameLoad"
            ></iframe>
        </div>
        <div v-else class="poc-empty">Select a file to render.</div>
    </div>
</template>

<style scoped>
.poc-page {
    display: flex;
    flex-direction: column;
    height: 100vh;
}
.poc-bar {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.6rem 1rem;
    border-bottom: 1px solid #ddd;
    background: #fafafa;
    font-size: 0.9rem;
}
.poc-bar select {
    padding: 0.25rem 0.5rem;
    min-width: 22rem;
}
.poc-error {
    color: #b00020;
}
.poc-frame-wrap {
    position: relative;
    flex: 1 1 auto;
    min-height: 0;
}
.poc-frame {
    width: 100%;
    height: 100%;
    border: 0;
}
.poc-loading {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.6rem;
    background: #fff;
    color: #555;
    font-size: 0.95rem;
    z-index: 1;
}
.poc-spinner {
    width: 1.1rem;
    height: 1.1rem;
    border: 2px solid #ccc;
    border-top-color: #555;
    border-radius: 50%;
    animation: poc-spin 0.8s linear infinite;
}
@keyframes poc-spin {
    to {
        transform: rotate(360deg);
    }
}
.poc-empty {
    padding: 2rem;
    color: #666;
}
</style>
